import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { flowApi } from '../../services/api';

// Helper function to identify medical specialties
const isMedicalSpecialty = (tag) => {
  const medicalSpecialties = [
    'neonatology', 'cardiology', 'respiratory', 'neurology',
    'gastroenterology', 'nephrology', 'endocrinology', 'hematology',
    'infectious'
  ];
  return medicalSpecialties.includes(tag.toLowerCase());
};

const SimilarFlowsSearch = ({
  query,
  onLoadFlow,
  disabled = false
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const searchSimilarFlows = async () => {
    if (!query || query.trim().length < 10) {
      setError('Please enter a more detailed query (at least 10 characters)');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required to search for similar flows');
        return;
      }

      const data = await flowApi.getRecommendations({
        query: query.trim(),
        max_recommendations: 5,
        min_similarity_score: 0.2,  // Lower threshold for hybrid scoring
        include_own_flows: false
      });

      setRecommendations(data.recommendations || []);
      
    } catch (err) {
      setError(err.message || 'Failed to search for similar flows');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadFlow = async (flowId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      // Get flow details
      const flowData = await flowApi.getFlow(flowId);

      // Record usage
      await flowApi.recordUsage(flowId);

      // Load the flow
      if (onLoadFlow) {
        onLoadFlow(flowData.flow);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to load flow');
    }
  };

  const getSimilarityColor = (score) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'info';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Search Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
          onClick={searchSimilarFlows}
          disabled={disabled || loading || !query || query.trim().length < 10}
          sx={{ borderRadius: 2 }}
          fullWidth
        >
          {loading ? 'Searching...' : 'Find Similar Reasoning Flows'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results Section */}
      {hasSearched && (
        <Box>
          {recommendations.length > 0 ? (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightbulbIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Similar Flows Found
                  </Typography>
                  <Chip 
                    label={recommendations.length} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                </Box>
                
                <IconButton
                  onClick={() => setExpanded(!expanded)}
                  size="small"
                >
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {recommendations.map((recommendation, index) => (
                    <SimilarFlowCard
                      key={recommendation.flow_id}
                      recommendation={recommendation}
                      onLoadFlow={() => handleLoadFlow(recommendation.flow_id)}
                      getSimilarityColor={getSimilarityColor}
                      formatDate={formatDate}
                      isMedicalSpecialty={isMedicalSpecialty}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          ) : (
            !loading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No similar flows found. Try adjusting your query or be the first to create a flow for this topic!
              </Alert>
            )
          )}
        </Box>
      )}
    </Box>
  );
};

const SimilarFlowCard = ({ recommendation, onLoadFlow, getSimilarityColor, formatDate, isMedicalSpecialty }) => {
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        borderRadius: 2,
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main'
        }
      }}
    >
      <CardContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {recommendation.original_query}
            </Typography>
            
            {recommendation.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {recommendation.description}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip
              label={`${Math.round(recommendation.similarity_score * 100)}% relevance`}
              size="small"
              color={getSimilarityColor(recommendation.similarity_score)}
              variant="outlined"
            />
            {recommendation.similarity_score >= 0.8 && (
              <Chip
                label="Highly Relevant"
                size="small"
                color="success"
                variant="filled"
                sx={{ fontSize: '0.7rem', height: 18 }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {recommendation.tags.slice(0, 6).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                // Highlight medical specialties
                ...(isMedicalSpecialty(tag) && {
                  backgroundColor: 'primary.50',
                  borderColor: 'primary.main',
                  color: 'primary.main'
                })
              }}
            />
          ))}
          {recommendation.tags.length > 6 && (
            <Chip
              label={`+${recommendation.tags.length - 6} more`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', opacity: 0.7 }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title={`Created by ${recommendation.created_by_username}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                  <PersonIcon sx={{ fontSize: 12 }} />
                </Avatar>
                <Typography variant="caption" color="text.secondary">
                  {recommendation.created_by_username}
                </Typography>
              </Box>
            </Tooltip>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 14 }} color="action" />
              <Typography variant="caption" color="text.secondary">
                {formatDate(recommendation.created_at)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon sx={{ fontSize: 14 }} color="action" />
              <Typography variant="caption" color="text.secondary">
                {recommendation.usage_count} uses
              </Typography>
            </Box>
          </Box>

          <Button
            size="small"
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={onLoadFlow}
            sx={{ borderRadius: 2 }}
          >
            Use Flow
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Plan: {recommendation.plan_summary}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SimilarFlowsSearch;
